import { Head, Link, router, usePage } from "@inertiajs/react"
import type { ReactNode } from "react"
import MatchLayout from "@/layouts/match-layout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Plus } from "lucide-react"

interface UserData {
  id: number
  name: string
  email: string
  role: string
  player_id: number | null
  player_name: string | null
  verified: boolean
}

const ROLE_BADGE: Record<string, string> = {
  admin: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  coach: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  player: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
}

export default function StaffUsersIndex() {
  const { users } = usePage().props as unknown as { users: UserData[] }

  function destroy(id: number) {
    if (confirm("Are you sure you want to delete this user?")) {
      router.delete(`/staff/users/${id}`)
    }
  }

  return (
    <>
      <Head title="Manage Users" />
      <div className="border-b bg-card">
        <div className="mx-auto max-w-4xl px-4 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Users</h1>
            <p className="mt-1 text-sm text-muted-foreground">Manage users and their roles</p>
          </div>
          <Link href="/staff/users/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-6">
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b text-left text-xs font-medium uppercase text-muted-foreground">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Player</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No users found. Create one to get started.
                    </td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user.id} className="border-b last:border-0">
                      <td className="px-4 py-3 text-sm font-medium">{user.name}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{user.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_BADGE[user.role] || ""}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{user.player_name || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          user.verified ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : "bg-yellow-100 text-yellow-800"
                        }`}>
                          {user.verified ? "Verified" : "Pending"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link href={`/staff/users/${user.id}/edit`} className="text-sm text-primary hover:underline">
                            Edit
                          </Link>
                          <button onClick={() => destroy(user.id)} className="text-sm text-destructive hover:underline">
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

StaffUsersIndex.layout = (page: ReactNode) => <MatchLayout>{page}</MatchLayout>
